import 'swiper/swiper-bundle.css'

import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { getCampaigns } from '@/api/campaigns/get-campaigns'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { env } from '@/env'
import { useHeadManager } from '@/lib/headManagerProvider'
import { faqs, homeData, homeStats, testimonials } from '@/mock/mock-data-home'

import { GetCampaigns } from '../campaigns/types'

export function Home() {
  const { setTitle } = useHeadManager()
  const navigate = useNavigate()

  const [campanhasAbertas, setCampanhasAbertas] = useState<GetCampaigns[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const getAllCampaigns = useMutation({
    mutationFn: getCampaigns,
    onSuccess: (data) => {
      setCampanhasAbertas(
        data.filter((campanha) => campanha.campanha.sorteado === false),
      )
      setIsLoading(false)
    },
    onError: (error) => {
      console.error('Erro ao buscar campanhas:', error)
      setIsLoading(false)
    },
  })

  useEffect(() => {
    setTitle('Início')
    getAllCampaigns.mutate()
  }, [setTitle])

  return (
    <div className="container mx-auto mt-5 p-4">
      {isLoading ? (
        <div className="mb-5 flex flex-col items-center">
          <Skeleton className="mb-4 h-10 w-3/4" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      ) : (
        <>
          <h1 className="text-primary mb-4 text-center text-4xl font-bold">
            {homeData.title}
          </h1>
          <p className="text-muted-foreground mb-5 text-center">
            {homeData.sub_title}
          </p>
        </>
      )}

      <div className="-mx-4 mb-15">
        {isLoading ? (
          <div className="flex justify-center">
            <Skeleton className="h-[420px] w-full rounded-lg" />
          </div>
        ) : (
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            speed={800}
            className="w-full"
          >
            {campanhasAbertas.map((rifa) => (
              <SwiperSlide key={rifa.campanha.id}>
                <Card className="shadow-md transition-all hover:shadow-xl">
                  <CardContent className="relative p-0">
                    <div className="relative">
                      <img
                        src={`${env.VITE_API_URL}${rifa.campanha.imagem}`}
                        alt={rifa.campanha.titulo}
                        className="mx-auto h-100 w-380 rounded-lg object-cover object-center"
                      />
                      <Button
                        className="absolute right-4 bottom-4 rounded-lg bg-green-600 px-4 py-2 text-white shadow-md hover:bg-green-700"
                        onClick={() =>
                          navigate(`/campanhas/${rifa.campanha.id}/checkout`)
                        }
                      >
                        Participar
                      </Button>
                    </div>
                  </CardContent>
                  <div className="mt-1 p-4 text-center">
                    <h2 className="text-xl font-semibold">
                      {rifa.campanha.titulo}
                    </h2>
                    <Badge className="mt-2">
                      R$ {Number(rifa.campanha.titulo_Valor).toFixed(2)}
                    </Badge>
                  </div>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      <h2 className="text-muted-foreground mb-20 text-center text-3xl font-bold">
        Como Funciona?
      </h2>
      <div className="relative mt-6 grid grid-cols-1 gap-6 text-center md:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="rounded-lg border p-4 shadow-md">
                <Skeleton className="mx-auto mb-3 h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))
          : homeData.howItWorks.map((step, index) => (
              <div key={index} className="rounded-lg border p-4 shadow-md">
                <h3 className="text-lg font-semibold">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.description}</p>
              </div>
            ))}
      </div>

      <h2 className="text-muted-foreground mt-30 mb-15 text-center text-3xl font-bold">
        Confira o que nossos clientes têm a dizer:
      </h2>
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} className="p-4 shadow-md">
                <CardContent>
                  <Skeleton className="mb-4 h-16 w-full" />
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-2">
                    <div>
                      <Skeleton className="mb-1 h-5 w-24" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-5 w-24" />
                  </div>
                </CardContent>
              </Card>
            ))
          : testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                style={{ backgroundColor: 'var(--primary)' }}
                className="p-4 text-white shadow-md"
              >
                <CardContent>
                  <p className="text-sm italic">
                    {'"'}
                    {testimonial.text}
                    {'"'}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-gray-700 pt-2">
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-xs text-white">
                        {testimonial.username}
                      </p>
                    </div>
                    <Button variant="link" className="text-white">
                      Ler Depoimento
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
      </div>

      <div className="mt-20 text-center">
        <div className="grid grid-cols-1 justify-items-center gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="max-w-xs">
                  <Skeleton className="mx-auto mb-2 h-6 w-3/4" />
                  <Skeleton className="mx-auto mt-2 h-10 w-20" />
                  <Skeleton className="mt-1 h-4 w-full" />
                </div>
              ))
            : homeStats.map((stat, idx) => (
                <div key={idx} className="max-w-xs">
                  <h4 className="text-lg font-semibold text-gray-800 sm:text-xl dark:text-slate-300">
                    {stat.title}
                  </h4>
                  <p className="text-primary-700 dark:text-primary-200 mt-2 text-4xl font-bold sm:mt-3 sm:text-5xl">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-gray-400">{stat.description}</p>
                </div>
              ))}
        </div>
      </div>

      <div
        className="pointer-events-none mt-16 flex justify-end opacity-80 select-none"
        aria-hidden="true"
      >
        <svg
          className="h-auto w-40 sm:w-72"
          width="1115"
          height="636"
          viewBox="0 0 1115 636"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.990203 279.321C-1.11035 287.334 3.68307 295.534 11.6966 297.634L142.285 331.865C150.298 333.965 158.497 329.172 160.598 321.158C162.699 313.145 157.905 304.946 149.892 302.845L33.8132 272.418L64.2403 156.339C66.3409 148.326 61.5475 140.127 53.5339 138.026C45.5204 135.926 37.3213 140.719 35.2207 148.733L0.990203 279.321ZM424.31 252.289C431.581 256.26 440.694 253.585 444.664 246.314C448.635 239.044 445.961 229.931 438.69 225.96L424.31 252.289ZM23.0706 296.074C72.7581 267.025 123.056 230.059 187.043 212.864C249.583 196.057 325.63 198.393 424.31 252.289L438.69 225.96C333.77 168.656 249.817 164.929 179.257 183.892C110.144 202.465 54.2419 243.099 7.92943 270.175L23.0706 296.074Z"
            fill="currentColor"
            className="fill-orange-500"
          />
          <path
            d="M451.609 382.417C446.219 388.708 446.95 398.178 453.241 403.567L555.763 491.398C562.054 496.788 571.524 496.057 576.913 489.766C582.303 483.474 581.572 474.005 575.281 468.615L484.15 390.544L562.222 299.413C567.612 293.122 566.881 283.652 560.59 278.263C554.299 272.873 544.829 273.604 539.44 279.895L451.609 382.417ZM837.202 559.655C841.706 566.608 850.994 568.593 857.947 564.09C864.9 559.586 866.885 550.298 862.381 543.345L837.202 559.655ZM464.154 407.131C508.387 403.718 570.802 395.25 638.136 410.928C704.591 426.401 776.318 465.66 837.202 559.655L862.381 543.345C797.144 442.631 718.724 398.89 644.939 381.709C572.033 364.734 504.114 373.958 461.846 377.22L464.154 407.131Z"
            fill="currentColor"
            className="fill-cyan-500"
          />
          <path
            d="M447.448 0.194357C439.203 -0.605554 431.87 5.43034 431.07 13.6759L418.035 148.045C417.235 156.291 423.271 163.623 431.516 164.423C439.762 165.223 447.095 159.187 447.895 150.942L459.482 31.5025L578.921 43.0895C587.166 43.8894 594.499 37.8535 595.299 29.6079C596.099 21.3624 590.063 14.0296 581.818 13.2297L447.448 0.194357ZM1086.03 431.727C1089.68 439.166 1098.66 442.239 1106.1 438.593C1113.54 434.946 1116.62 425.96 1112.97 418.521L1086.03 431.727ZM434.419 24.6572C449.463 42.934 474.586 81.0463 521.375 116.908C568.556 153.07 637.546 187.063 742.018 200.993L745.982 171.256C646.454 157.985 582.444 125.917 539.625 93.0974C496.414 59.978 474.537 26.1903 457.581 5.59138L434.419 24.6572ZM742.018 200.993C939.862 227.372 1054.15 366.703 1086.03 431.727L1112.97 418.521C1077.85 346.879 956.138 199.277 745.982 171.256L742.018 200.993Z"
            fill="currentColor"
            className="fill-primary-700"
          />
        </svg>
      </div>

      <Separator className="my-10" />

      <h2 className="text-muted-foreground text-center text-3xl font-bold">
        Perguntas Frequentes
      </h2>
      <div className="mt-6">
        <Accordion
          type="single"
          collapsible
          className="mx-auto w-full max-w-3xl"
        >
          {isLoading
            ? Array.from({ length: 3 }).map((_, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}>
                  <AccordionTrigger>
                    <Skeleton className="h-5 w-full" />
                  </AccordionTrigger>
                  <AccordionContent>
                    <Skeleton className="h-16 w-full" />
                  </AccordionContent>
                </AccordionItem>
              ))
            : faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}>
                  <AccordionTrigger>{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
        </Accordion>
      </div>

      <style>
        {`
          .swiper-button-next,
          .swiper-button-prev {
            color: #22c55e !important;
          }
        `}
      </style>
    </div>
  )
}
